import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supportedLanguages, t as translate } from "../data/translations";
import { getProductContent } from "../data/productContent";

const LanguageContext = createContext(null);

const STORAGE_KEY = "me_lang";
const DEFAULT_LOCALE = "en";
const FALLBACK_LOCALE = "en";

const isSupported = (code) => Boolean(supportedLanguages.find((l) => l.code === code));

const readSavedLocale = () => {
  if (typeof window === "undefined") return null;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved && isSupported(saved) ? saved : null;
  } catch {
    return null;
  }
};

export const LanguageProvider = ({ children }) => {
  // Default = English on every first visit.
  // localStorage is only honoured if the user manually picked a language earlier.
  // Browser language is never auto-detected.
  const [lang, setLangState] = useState(() => readSavedLocale() || DEFAULT_LOCALE);

  const meta =
    supportedLanguages.find((l) => l.code === lang) ||
    supportedLanguages.find((l) => l.code === FALLBACK_LOCALE);
  const dir = meta.dir;

  // Reflect lang/dir on the document. We deliberately do NOT touch localStorage here
  // — initial mounts must not persist the default English locale.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    document.body.dir = dir;
  }, [lang, dir]);

  // Public setter — this is the ONLY path that writes to localStorage,
  // so the value is saved only after a manual user choice.
  const setLang = useCallback((next) => {
    if (!isSupported(next)) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore quota / privacy mode errors */
    }
  }, []);

  const t = useCallback((key) => translate(lang, key), [lang]);
  const productContent = useCallback((id) => getProductContent(lang, id), [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, dir, meta, t, productContent }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be inside LanguageProvider");
  return ctx;
};
